import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Search, User, CreditCard, Activity, DollarSign, Eye, Mail, PlusCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { handleEdgeFunctionError, formatErrorForToast } from "@/lib/errorHandler";
import { UserViewMode } from "./UserViewMode";

interface UserDetails {
  profile: any;
  credits: any;
  tasks: any[];
  tasksCount: number;
  purchases: any[];
  purchasesCount: number;
  transactions: any[];
}

export function UserManager() {
  const { toast } = useToast();
  const { session } = useAuth();
  const [searchEmail, setSearchEmail] = useState("");
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [creditAmount, setCreditAmount] = useState("");
  const [adjustReason, setAdjustReason] = useState("");
  const [processing, setProcessing] = useState(false);
  const [viewModeOpen, setViewModeOpen] = useState(false);
  const [viewModeData, setViewModeData] = useState<any>(null);
  const [loadingViewMode, setLoadingViewMode] = useState(false);

  const searchUser = async () => {
    if (!searchEmail.trim()) {
      toast({
        title: "Erro",
        description: "Digite um email para buscar.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      // Search user via admin-actions to bypass RLS
      const { data: searchData, error: searchError } = await supabase.functions.invoke("admin-actions", {
        body: { action: "search_user", email: searchEmail.trim() },
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });

      if (searchError || searchData?.error) {
        const errorInfo = handleEdgeFunctionError(searchError, searchData, 'admin');
        toast(formatErrorForToast(errorInfo));
        setLoading(false);
        return;
      }

      const profiles = searchData?.profiles || [];
      if (profiles.length === 0) {
        toast({
          title: "Usuário não encontrado",
          description: "Nenhum usuário encontrado com este email.",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      const userId = profiles[0].id;

      // Get full user details via admin function
      const { data, error } = await supabase.functions.invoke("admin-actions", {
        body: {
          action: "get_user_details",
          userId,
        },
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        },
      });

      if (error || data?.error) {
        const errorInfo = handleEdgeFunctionError(error, data, 'admin');
        toast(formatErrorForToast(errorInfo));
        setLoading(false);
        return;
      }

      setUserDetails(data);
    } catch (error: any) {
      console.error("Error searching user:", error);
      const errorInfo = handleEdgeFunctionError(error, null, 'admin');
      toast(formatErrorForToast(errorInfo));
    } finally {
      setLoading(false);
    }
  };

  const adjustCredits = async () => {
    if (!userDetails || !creditAmount || !adjustReason.trim()) {
      toast({
        title: "Erro",
        description: "Preencha todos os campos.",
        variant: "destructive",
      });
      return;
    }

    const amount = parseInt(creditAmount);
    if (isNaN(amount) || amount === 0) {
      toast({
        title: "Erro",
        description: "Digite um valor válido (positivo para adicionar, negativo para remover).",
        variant: "destructive",
      });
      return;
    }

    setProcessing(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-actions", {
        body: {
          action: "adjust_credits",
          userId: userDetails.profile.id,
          amount,
          reason: adjustReason,
        },
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        },
      });

      if (error || data?.error) {
        const errorInfo = handleEdgeFunctionError(error, data, 'admin');
        toast(formatErrorForToast(errorInfo));
        return;
      }

      toast({
        title: "Créditos ajustados",
        description: `Novo saldo: ${data.newAmount} créditos`,
      });

      setAdjustDialogOpen(false);
      setCreditAmount("");
      setAdjustReason("");

      // Reload user details
      searchUser();
    } catch (error: any) {
      console.error("Error adjusting credits:", error);
      const errorInfo = handleEdgeFunctionError(error, null, 'admin');
      toast(formatErrorForToast(errorInfo));
    } finally {
      setProcessing(false);
    }
  };

  const openViewMode = async () => {
    if (!userDetails) return;

    setLoadingViewMode(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-actions", {
        body: {
          action: "get_user_view_data",
          userId: userDetails.profile.id,
        },
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        },
      });

      if (error || data?.error) {
        const errorInfo = handleEdgeFunctionError(error, data, 'admin');
        toast(formatErrorForToast(errorInfo));
        return;
      }

      setViewModeData(data);
      setViewModeOpen(true);
    } catch (error: any) {
      console.error("Error loading view mode:", error);
      const errorInfo = handleEdgeFunctionError(error, null, 'admin');
      toast(formatErrorForToast(errorInfo));
    } finally {
      setLoadingViewMode(false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Gestão de Usuários
          </CardTitle>
          <CardDescription>Busque e gerencie usuários do sistema</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Digite o email do usuário..."
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && searchUser()}
                className="pl-9"
              />
            </div>
            <Button onClick={searchUser} disabled={loading}>
              {loading ? "Buscando..." : "Buscar"}
            </Button>
          </div>

          {userDetails && (
            <div className="space-y-4 pt-4 border-t">
              {/* Quick Actions Bar */}
              <div className="flex flex-wrap gap-2 p-3 bg-muted/50 rounded-lg">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openViewMode}
                  disabled={loadingViewMode}
                  className="flex items-center gap-2"
                >
                  <Eye className="w-4 h-4" />
                  {loadingViewMode ? "Carregando..." : "Ver como usuário"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAdjustDialogOpen(true)}
                  className="flex items-center gap-2"
                >
                  <PlusCircle className="w-4 h-4" />
                  Dar créditos
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  asChild
                  className="flex items-center gap-2"
                >
                  <a href={`mailto:${searchEmail}`}>
                    <Mail className="w-4 h-4" />
                    Enviar email
                  </a>
                </Button>
              </div>

              <div className="grid gap-4 md:grid-cols-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Perfil
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-1">
                      <p className="font-medium">{userDetails.profile.full_name || "Sem nome"}</p>
                      <p className="text-xs text-muted-foreground">
                        ID: {userDetails.profile.id.slice(0, 8)}...
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Criado: {new Date(userDetails.profile.created_at).toLocaleDateString("pt-BR")}
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <CreditCard className="w-4 h-4" />
                      Créditos
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <p className="text-2xl font-bold">{userDetails.credits?.available_credits || 0}</p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setAdjustDialogOpen(true)}
                        className="w-full"
                      >
                        Ajustar
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Activity className="w-4 h-4" />
                      Tasks
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold">{userDetails.tasksCount || 0}</p>
                    <p className="text-xs text-muted-foreground mt-1">Total criadas</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <DollarSign className="w-4 h-4" />
                      Compras
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold">{userDetails.purchasesCount || 0}</p>
                    <p className="text-xs text-muted-foreground mt-1">Total realizadas</p>
                  </CardContent>
                </Card>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Tasks Recentes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {userDetails.tasks.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          Nenhuma task encontrada
                        </p>
                      ) : (
                        userDetails.tasks.map((task: any) => (
                          <div key={task.id} className="flex items-center justify-between p-2 border rounded">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{task.product_name}</p>
                              <p className="text-xs text-muted-foreground">
                                {new Date(task.created_at).toLocaleDateString("pt-BR")}
                              </p>
                            </div>
                            <span
                              className={`text-xs px-2 py-1 rounded ${task.status === "completed"
                                ? "bg-green-100 text-green-800"
                                : task.status === "error"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-blue-100 text-blue-800"
                                }`}
                            >
                              {task.status}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Transações de Crédito</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {userDetails.transactions.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          Nenhuma transação encontrada
                        </p>
                      ) : (
                        userDetails.transactions.map((tx: any) => (
                          <div key={tx.id} className="flex items-center justify-between p-2 border rounded">
                            <div className="flex-1">
                              <p className="text-sm font-medium">{tx.description || tx.type}</p>
                              <p className="text-xs text-muted-foreground">
                                {new Date(tx.created_at).toLocaleDateString("pt-BR")}
                              </p>
                            </div>
                            <span
                              className={`text-sm font-bold ${tx.amount > 0 ? "text-green-600" : "text-red-600"
                                }`}
                            >
                              {tx.amount > 0 ? "+" : ""}
                              {tx.amount}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={adjustDialogOpen} onOpenChange={setAdjustDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajustar Créditos</DialogTitle>
            <DialogDescription>
              Adicione (valor positivo) ou remova (valor negativo) créditos do usuário.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Saldo Atual</Label>
              <p className="text-2xl font-bold">{userDetails?.credits?.available_credits || 0}</p>
            </div>
            <div>
              <Label htmlFor="credit-amount">Quantidade *</Label>
              <Input
                id="credit-amount"
                type="number"
                placeholder="Ex: 10 ou -5"
                value={creditAmount}
                onChange={(e) => setCreditAmount(e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Use valores positivos para adicionar, negativos para remover
              </p>
            </div>
            <div>
              <Label htmlFor="adjust-reason">Motivo *</Label>
              <Textarea
                id="adjust-reason"
                placeholder="Descreva o motivo do ajuste..."
                value={adjustReason}
                onChange={(e) => setAdjustReason(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjustDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={adjustCredits}
              disabled={processing || !creditAmount || !adjustReason.trim()}
            >
              {processing ? "Processando..." : "Confirmar Ajuste"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User View Mode Portal */}
      {viewModeOpen && viewModeData && (
        <UserViewMode
          data={viewModeData}
          onClose={() => {
            setViewModeOpen(false);
            setViewModeData(null);
          }}
        />
      )}
    </>
  );
}
